// SSE Connection wrapper for game/adventure event streams

/**
 * @typedef {object} SSEEvent
 * @property {number} id
 * @property {string} eventType
 * @property {string} [summary]
 * @property {Array<import('../types/jsdoc-types.js').Change>} changes
 * @property {number} prevEventId
 * @property {string} [gameEventId]
 */

/**
 * @typedef {object} SSEConnectionOptions
 * @property {(event: SSEEvent) => void} onEvent
 * @property {() => void} [onConnected]
 * @property {() => void} [onDisconnected]
 * @property {(error: Error) => void} [onError]
 * @property {(data: any) => void} [onSync] - Called when the server sends a game:sync frame
 * @property {number} [reconnectBackoffMs] - Override the reconnect back-off (tests inject a short one)
 */

/**
 * @typedef {() => Promise<string>} GetAccessToken
 */

// Back-off used when EventSource reports CLOSED and we need to rebuild the URL
// with a freshly-fetched token. Small enough to feel instant, long enough to
// avoid a tight loop against a hard-down server.
const RECONNECT_BACKOFF_MS = 1000;

/**
 * Create an SSE connection for real-time game events
 * @param {'game' | 'adventure' | 'game-spectate'} entityType
 * @param {string} entityId
 * @param {string} backendUrl
 * @param {SSEConnectionOptions} options
 * @returns {{
 *   isConnected: boolean,
 *   connectionError: string | null,
 *   connect: (getAccessToken: GetAccessToken, lastEventId: number | null) => Promise<void>,
 *   disconnect: () => void,
 *   reconnect: (getAccessToken: GetAccessToken, lastEventId: number | null) => void
 * }}
 */
export function createSSEConnection(entityType, entityId, backendUrl, options) {
  /** @type {EventSource | null} */
  let eventSource = null;
  let isConnected = false;
  /** @type {string | null} */
  let connectionError = null;
  let isIntentionalDisconnect = false;

  /**
   * Connect to the SSE endpoint. Pulls a fresh access token via the
   * supplied async function so token rotation across long sessions is
   * handled transparently — both at first connect and on reconnect.
   * @param {GetAccessToken} getAccessToken
   * @param {number | null} lastEventId
   */
  const connect = async (getAccessToken, lastEventId) => {
    if (eventSource) {
      console.warn('Already connected');
      return;
    }

    isIntentionalDisconnect = false;

    let accessToken;
    try {
      accessToken = await getAccessToken();
    } catch (error) {
      console.error('Failed to fetch access token for SSE:', error);
      connectionError = /** @type {Error} */ (error).message;
      options.onError?.(/** @type {Error} */ (error));
      return;
    }

    if (!accessToken) {
      console.warn('No access token available');
      connectionError = 'No access token available';
      return;
    }

    // Build URL with query parameters based on entity type
    let endpoint;
    if (entityType === 'game') {
      endpoint = `/api/games/${entityId}/events-stream`;
    } else if (entityType === 'game-spectate') {
      endpoint = `/api/games/${entityId}/spectate-stream`;
    } else {
      endpoint = `/api/adventures/${entityId}/events-stream`;
    }

    let url = `${backendUrl}${endpoint}?token=${encodeURIComponent(accessToken)}`;
    if (lastEventId !== null) {
      url += `&since=${lastEventId}`;
    }

    console.debug('Connecting to', url.replace(/token=[^&]+/, 'token=***'));

    try {
      eventSource = new EventSource(url);

      eventSource.onopen = () => {
        console.info('SSE Connected');
        isConnected = true;
        connectionError = null;
        options.onConnected?.();
      };

      eventSource.onmessage = (event) => {
        console.debug('Received SSE message, data length:', event.data?.length);
        try {
          const data = JSON.parse(event.data);
          console.debug('Parsed SSE event:', data.eventType, 'id:', data.id);
          options.onEvent(data);
        } catch (error) {
          console.error('Failed to parse event:', error, 'raw data:', event.data?.substring(0, 200));
        }
      };

      // game:sync is the rebuild cache-invalidation primitive. The server
      // sends it as the very first frame on every new SSE connection. The
      // payload carries the current games.generation; if the client's cached
      // generation no longer matches, it must flush local state and
      // resubscribe from event 0. Today the handler just logs and forwards
      // the event upward via options.onSync — the caller decides what to do.
      eventSource.addEventListener('game:sync', (event) => {
        try {
          const data = JSON.parse(/** @type {MessageEvent} */ (event).data);
          console.info('SSE game:sync', data);
          options.onSync?.(data);
        } catch (error) {
          console.error('Failed to parse game:sync event:', error);
        }
      });

      eventSource.onerror = () => {
        // Don't log errors if we're intentionally disconnecting
        if (isIntentionalDisconnect) {
          return;
        }

        isConnected = false;

        // EventSource auto-reconnect reuses the original URL, so the token
        // baked into it is the one we had when we first connected. After
        // ~15 minutes that token is stale and the server closes the
        // stream. Detect CLOSED and rebuild the URL with a freshly-fetched
        // token via reconnect(); EventSource's other error states (e.g.
        // transient network blip) self-heal via its built-in retry.
        if (eventSource?.readyState === EventSource.CLOSED) {
          console.warn('SSE Connection closed by server, will reconnect with fresh token');
          connectionError = 'Connection closed by server';
          options.onError?.(new Error('SSE connection closed'));
          reconnect(getAccessToken, lastEventId);
        } else {
          console.warn('SSE Connection error, will retry');
          connectionError = 'Connection error';
          options.onError?.(new Error('SSE connection error'));
        }
      };
    } catch (error) {
      console.error('Failed to create EventSource:', error);
      connectionError = /** @type {Error} */ (error).message;
      options.onError?.(/** @type {Error} */ (error));
    }
  };

  const disconnect = () => {
    if (eventSource) {
      isIntentionalDisconnect = true;
      eventSource.close();
      eventSource = null;
      isConnected = false;
      // Don't call onDisconnected callback for intentional disconnects
    }
  };

  /**
   * Reconnect to the SSE endpoint. Pulls a fresh access token via the
   * supplied function so a rotated/refreshed token is used for the new
   * connection.
   * @param {GetAccessToken} getAccessToken
   * @param {number | null} lastEventId
   */
  const reconnect = (getAccessToken, lastEventId) => {
    console.info('SSE Reconnecting');
    disconnect();
    setTimeout(() => {
      // connect() is async; ignore the promise. Errors are surfaced via
      // options.onError, not the caller.
      void connect(getAccessToken, lastEventId);
    }, options.reconnectBackoffMs ?? RECONNECT_BACKOFF_MS);
  };

  return {
    get isConnected() {
      return isConnected;
    },
    get connectionError() {
      return connectionError;
    },
    connect,
    disconnect,
    reconnect,
  };
}
